import { useContext, useState, useEffect } from 'react';
import { Button, Col, FormGroup, Input, Label, Row, Table } from 'reactstrap';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import { isAdmin } from '../../../helpers';
import confirmAlertWithForm from '../../../helpers/confirmAlertWithForm';
import { toast } from 'react-toastify';
import PayrollAccountingCodes from '../../../services/PayrollAccountingCodes';

type AccountingCodesFormProps = {
  isOpen: boolean;
};

const AccountingCodesForm = ({ isOpen }: AccountingCodesFormProps) => {
  const { token, currentFranchisee, currentSchool, user }: any = useContext(AuthContext);
  const [accountingCodesList, setAccountingCodesList] = useState([] as any[]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(new Date().getTime());
  const [loading, setLoading] = useState(false);
  const canDelete = isAdmin(user);

  useEffect(() => {
    if (isOpen) {
      const listData = async () => {
        try {
          const result = await PayrollAccountingCodes(token).list(currentFranchisee?.value);
          setAccountingCodesList(result.data || []);
        } catch (e: any) {
          toast.error(e?.response?.data?.message || 'Failed to load accounting codes');
        }
      };

      listData();
    }
  }, [isOpen, lastUpdatedAt]);

  const deleteSettings = async (e: React.FormEvent, id: string) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    try {
      await PayrollAccountingCodes(token).delete(id);
      toast.success('Accounting code deleted successfully');
      setLastUpdatedAt(new Date().getTime());
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Failed to delete accounting code');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const openCreatingForm = () => {
    confirmAlertWithForm({
      title: 'Create New Accounting Code',
      messageType: 'SAVE',
      message: ' ',
      children: (
        <Row>
          <Col lg="12">
            <FormGroup>
              <Label className="form-control-label">Franchisee</Label>
              <Input name="franchiseeId" value={currentFranchisee?.label || ''} disabled />
            </FormGroup>
          </Col>
          <Col lg="12">
            <FormGroup>
              <Label className="form-control-label">School</Label>
              <Input name="schoolId" value={currentSchool?.label || ''} disabled />
            </FormGroup>
          </Col>
          <Col lg="12">
            <FormGroup>
              <Label className="form-control-label">Name</Label>
              <Input name="name" required />
            </FormGroup>
          </Col>
        </Row>
      ),
      onSubmit: async (e: any) => {
        e.preventDefault();
        setLoading(true);
        try {
          const data = new FormData(e.target as HTMLFormElement);
          const body = {
            franchiseeId: currentFranchisee?.value || '',
            name: data.get('name') as string,
          };
          await PayrollAccountingCodes(token).create(body);
          toast.success('Accounting code created successfully');
          setLastUpdatedAt(new Date().getTime());
        } catch (e: any) {
          const message = String(e?.response?.data?.message || 'Failed to create accounting code');
          toast.error(message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <>
      <Row>
        <Col>
          <Button
            color="primary"
            onClick={() => {
              openCreatingForm();
            }}
          >
            <i className="fas fa-plus" />
            <span>Create New</span>
          </Button>
        </Col>
      </Row>
      <Table className="align-items-center table-flush mt-2" responsive>
        <tbody>
          <tr>
            <th>Title</th>
            <th>Action</th>
          </tr>
          {accountingCodesList?.length === 0 && (
            <tr>
              <td colSpan={2} className="text-center">
                Data not found
              </td>
            </tr>
          )}
          {accountingCodesList.map((s: any) => (
            <tr key={`accounting-code-${s.id}`}>
              <td>{s.name}</td>
              <td>
                {canDelete && (
                  <i
                    className="far fa-trash-alt text-danger"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => deleteSettings(e, s.id)}
                  ></i>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default AccountingCodesForm;
